/* ============================================================
   BLUND — MOTION ENGINE
   Lenis smooth-scroll + GSAP ScrollTrigger.
   - Cinematic reveal section (.vmr): hybrid desktop scrubbed
     video / mobile + no-video GSAP layered choreography.
   - Site-wide [data-parallax] + [data-reveal] utilities.
   Loaded with defer. Fully reduced-motion & no-dependency safe.
   ============================================================ */
(function () {
  "use strict";

  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var hasGsap = (typeof gsap !== "undefined");
  var hasST = hasGsap && (typeof ScrollTrigger !== "undefined");

  // If GSAP failed to load, make sure nothing stays hidden.
  function revealAllStatic() {
    document.querySelectorAll("[data-reveal]").forEach(function (el) {
      el.style.opacity = "1"; el.style.transform = "none";
    });
  }
  if (!hasGsap) { revealAllStatic(); return; }
  if (hasST) gsap.registerPlugin(ScrollTrigger);

  var lenis = null;

  /* ---------- Lenis smooth scroll ---------- */
  function initLenis() {
    if (reduced || typeof Lenis === "undefined") return null;
    var l = new Lenis({ lerp: 0.1, smoothWheel: true, wheelMultiplier: 1, touchMultiplier: 1.6 });
    if (hasST) {
      l.on("scroll", ScrollTrigger.update);
      gsap.ticker.add(function (t) { l.raf(t * 1000); });
      gsap.ticker.lagSmoothing(0);
    } else {
      var raf = function (t) { l.raf(t); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
    return l;
  }

  /* ---------- Page-wide reveals (opt-in via [data-reveal]) ---------- */
  function initReveals() {
    var els = gsap.utils.toArray("[data-reveal]");
    if (!els.length) return;
    if (reduced || !hasST) { gsap.set(els, { autoAlpha: 1, y: 0 }); return; }
    gsap.set(els, { autoAlpha: 0, y: 28 });
    ScrollTrigger.batch(els, {
      start: "top 85%",
      onEnter: function (batch) {
        gsap.to(batch, { autoAlpha: 1, y: 0, duration: 0.9, ease: "power3.out", stagger: 0.12, overwrite: true });
      }
    });
  }

  /* ---------- Parallax ---------- */
  function initParallax() {
    if (reduced || !hasST) return;
    /* translate parallax — use only on overscan/cover backgrounds */
    gsap.utils.toArray("[data-parallax]").forEach(function (el) {
      if (el._vparallax) return; el._vparallax = true;
      var amt = parseFloat(el.getAttribute("data-parallax")) || 40;
      gsap.fromTo(el, { y: -amt }, {
        y: amt, ease: "none",
        scrollTrigger: { trigger: el.closest("section") || el, start: "top bottom", end: "bottom top", scrub: true }
      });
    });
    /* scale parallax — safe inside object-fit:cover frames (no layout shift) */
    gsap.utils.toArray("[data-parallax-scale]").forEach(function (el) {
      if (el._vpscale) return; el._vpscale = true;
      var to = parseFloat(el.getAttribute("data-parallax-scale")) || 1.08;
      gsap.fromTo(el, { scale: 1 }, {
        scale: to, ease: "none",
        scrollTrigger: { trigger: el.closest("section") || el, start: "top bottom", end: "bottom top", scrub: true }
      });
    });
  }

  /* ---------- Cinematic reveal ---------- */
  function initCinematic(root) {
    if (!root || root._vmrInit) return;
    root._vmrInit = true;

    var sticky    = root.querySelector("[data-vmr-sticky]");
    var video     = root.querySelector("[data-vmr-video]");
    var layers    = root.querySelector("[data-vmr-layers]");
    var bottle    = root.querySelector("[data-vmr-bottle]");
    var cap       = root.querySelector("[data-vmr-cap]");
    var capsHost  = root.querySelector("[data-vmr-gummies]");
    var goldline  = root.querySelector("[data-vmr-goldline]");
    var stepIntro = root.querySelector('[data-vmr-step="intro"]');
    var stepIngs  = root.querySelector('[data-vmr-step="ings"]');
    var stepOutro = root.querySelector('[data-vmr-step="outro"]');
    var ings      = gsap.utils.toArray(root.querySelectorAll(".vmr__ing"));
    var ingsHead  = root.querySelector("[data-vmr-ings-head]");
    var scrollcue = root.querySelector(".vmr__scrollcue");
    if (!sticky) return;

    var videoSrc  = (root.getAttribute("data-video") || "").trim();
    var lengthPct = parseInt(root.getAttribute("data-scroll-length") || "400", 10);
    var isDesktop = window.matchMedia("(min-width: 750px)").matches;
    if (!isDesktop) lengthPct = Math.round(lengthPct * 0.7); // snappier reveal under a thumb
    var useVideo  = isDesktop && !!videoSrc && !!video;

    /* Reduced motion: static composed hero (CSS already collapses pin) */
    if (reduced) {
      if (video) video.style.display = "none";
      if (layers) gsap.set(layers, { autoAlpha: 1 });
      gsap.set(ings, { autoAlpha: 1, y: 0 });
      return;
    }

    if (useVideo) {
      if (layers) layers.style.display = "none";
      video.src = videoSrc;
      video.load();
    } else if (video) {
      video.style.display = "none";
    }

    /* falling gummies (layered path) */
    var gummies = [];
    if (!useVideo && capsHost) {
      for (var i = 0; i < 7; i++) {
        var d = document.createElement("div");
        d.className = "vmr__cap-dot";
        d.style.left = (38 + ((i * 4663) % 24)) + "%";
        d.style.top = (26 + ((i * 911) % 8)) + "%";
        capsHost.appendChild(d);
        gummies.push(d);
      }
    }

    gsap.set(stepIngs, { autoAlpha: 0 });
    gsap.set(stepOutro, { autoAlpha: 0 });
    gsap.set(ings, { autoAlpha: 0, y: 24 });
    if (ingsHead) gsap.set(ingsHead, { autoAlpha: 0 });
    if (goldline) gsap.set(goldline, { opacity: 0 });

    var vh = function () { return window.innerHeight; };

    var tl = gsap.timeline({
      defaults: { ease: "none" },
      scrollTrigger: {
        trigger: root, start: "top top", end: "+=" + lengthPct + "%",
        scrub: 1, pin: sticky, pinSpacing: true, anticipatePin: 1, invalidateOnRefresh: true
      }
    });

    /* BEAT 1 — intro hold → fade in as we push in */
    if (scrollcue) tl.to(scrollcue, { autoAlpha: 0, duration: 0.6 }, 0.2);
    if (stepIntro) tl.to(stepIntro, { autoAlpha: 0, y: -50, duration: 1.4 }, 0.6);

    /* BEAT 2–3 — rotation + camera push */
    if (useVideo) {
      tl.fromTo(video, { scale: 1 }, { scale: 1.12, duration: 6 }, 0);
    } else if (bottle) {
      tl.fromTo(bottle, { scale: 1, rotate: 0, y: "0%" }, { scale: 1.5, rotate: 3, y: "6%", duration: 4 }, 0);
    }

    /* BEAT 4 — cap unscrew (only when a cap layer exists) */
    if (!useVideo && cap) {
      tl.to(cap, { y: "-130%", rotate: 75, duration: 1.3, ease: "power2.inOut" }, 3.4);
    }

    /* BEAT 5 — gummies fall */
    if (!useVideo && gummies.length) {
      tl.to(gummies, { autoAlpha: 1, duration: 0.3, stagger: 0.12 }, 4.1);
      tl.to(gummies, { y: function () { return vh() * 0.55; }, rotate: "+=200", duration: 2.4, stagger: 0.14, ease: "power1.in" }, 4.2);
      tl.to(gummies, { autoAlpha: 0, duration: 0.6, stagger: 0.1 }, 6.2);
    }

    /* BEAT 6 — ingredient reveal */
    if (!useVideo && bottle) tl.to(bottle, { scale: 1.18, autoAlpha: 0.42, duration: 1 }, 4.4);
    else if (useVideo) tl.to(video, { autoAlpha: 0.4, duration: 1 }, 4.4);
    if (goldline) tl.to(goldline, { opacity: 1, duration: 0.6 }, 4.4);
    if (ingsHead) tl.to(ingsHead, { autoAlpha: 1, duration: 0.5 }, 4.6);
    if (stepIngs) tl.to(stepIngs, { autoAlpha: 1, duration: 0.3 }, 4.6);
    tl.to(ings, { autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.28 }, 4.9);
    if (stepIngs) tl.to(stepIngs, { autoAlpha: 0, duration: 0.8 }, 7.8);
    if (ingsHead) tl.to(ingsHead, { autoAlpha: 0, duration: 0.6 }, 7.8);

    /* BEAT 7 — product return + outro */
    if (!useVideo && bottle) tl.to(bottle, { scale: 1, rotate: 0, y: "0%", autoAlpha: 1, duration: 1.4, ease: "power2.out" }, 7.8);
    else if (useVideo) tl.to(video, { scale: 1, autoAlpha: 1, duration: 1.4, ease: "power2.out" }, 7.8);
    if (goldline) tl.to(goldline, { opacity: 0, duration: 0.8 }, 8.0);
    if (stepOutro) tl.fromTo(stepOutro, { autoAlpha: 0, y: 40 }, { autoAlpha: 1, y: 0, duration: 1.2, ease: "power2.out" }, 8.6);

    /* video scrub */
    if (useVideo) {
      var attach = function () {
        ScrollTrigger.create({
          trigger: root, start: "top top", end: "+=" + lengthPct + "%", scrub: 1,
          onUpdate: function (self) {
            if (video.duration) {
              var t = self.progress * video.duration;
              if (Math.abs(video.currentTime - t) > 0.03) video.currentTime = t;
            }
          }
        });
      };
      if (video.readyState >= 1) attach();
      else video.addEventListener("loadedmetadata", attach, { once: true });
    }

    /* Header: blend into the dark cinematic hero while it's in view, solid after. */
    var vHeader = document.querySelector("[data-vita-header]");
    if (vHeader) {
      var setDark = function (on) { vHeader.classList.toggle("vita-header--on-dark", on); };
      setDark(true);
      ScrollTrigger.create({
        trigger: root, start: "top top", end: "+=" + lengthPct + "%",
        onToggle: function (self) { setDark(self.isActive); }
      });
    }
  }

  /* ---------- boot ---------- */
  function boot() {
    document.documentElement.classList.add("js");
    lenis = initLenis();
    var vmr = document.querySelector("[data-vmr]");
    if (vmr) initCinematic(vmr);
    initReveals();
    initParallax();
    if (hasST) {
      window.addEventListener("load", function () { ScrollTrigger.refresh(); });
      var rt; window.addEventListener("resize", function () { clearTimeout(rt); rt = setTimeout(function () { ScrollTrigger.refresh(); }, 200); });
    }
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  /* ---------- Theme Editor support ---------- */
  document.addEventListener("shopify:section:load", function (e) {
    var vmr = (e.target && e.target.querySelector) ? e.target.querySelector("[data-vmr]") : null;
    if (vmr) { vmr._vmrInit = false; initCinematic(vmr); }
    initReveals(); initParallax();
    if (hasST) ScrollTrigger.refresh();
  });
})();
